/**
 * TypeScript Interfaces for Voice Cloning & TTS Feature
 * Feature: Voice Cloning with ElevenLabs Integration
 */

import { Id } from '../../convex/_generated/dataModel';

// ============================================================================
// Voice Settings
// ============================================================================

/**
 * Voice settings for TTS generation
 * Maps to ElevenLabs voice settings parameters
 */
export interface IVoiceSettings {
  stability: number; // 0-1, lower = more variable
  similarityBoost: number; // 0-1, higher = closer to original
  style: number; // 0-1, style exaggeration
  useSpeakerBoost: boolean; // Enhance speaker similarity
}

// ============================================================================
// Voice Status Types
// ============================================================================

/**
 * Possible statuses for a cloned voice
 */
export type VoiceStatus = 'creating' | 'active' | 'failed' | 'deleted';

/**
 * Possible statuses for voiceover generation
 */
export type VoiceoverStatus = 'pending' | 'generating' | 'completed' | 'failed';

// ============================================================================
// Default Voice Types
// ============================================================================

/**
 * Use case categories for default voices
 */
export type DefaultVoiceUseCase =
  | 'narration'
  | 'audiobook'
  | 'education'
  | 'social_media'
  | 'business'
  | 'dramatic'
  | 'podcast'
  | 'gaming'
  | 'documentary';

/**
 * Default (pre-made) ElevenLabs voice
 * These are built-in voices that don't require cloning
 */
export interface IDefaultVoice {
  /** ElevenLabs voice ID */
  id: string;
  /** Display name */
  name: string;
  /** Voice description */
  description: string;
  /** Voice gender */
  gender: 'male' | 'female';
  /** Voice accent */
  accent: string;
  /** Recommended use case */
  useCase: DefaultVoiceUseCase;
  /** URL to preview audio */
  previewUrl: string;
}

// ============================================================================
// Cloned Voice Types
// ============================================================================

/**
 * Cloned voice entity from database
 * Represents an ElevenLabs cloned voice owned by a user
 */
export interface IClonedVoice {
  _id: Id<'clonedVoices'>;
  _creationTime: number;
  userId: Id<'users'>;
  elevenLabsVoiceId: string;
  name: string;
  description?: string;
  previewUrl?: string;
  voiceSettings: IVoiceSettings;
  sampleCount: number;
  status: VoiceStatus;
  errorMessage?: string;
  createdAt: number;
  updatedAt: number;
  lastUsedAt?: number;
  usageCount: number;
}

/**
 * Form input for voice cloning
 */
export interface IVoiceCloneFormInput {
  name: string;
  description?: string;
  audioFiles: File[];
}

/**
 * Audio file prepared for upload (base64 encoded)
 */
export interface IAudioFileUpload {
  base64: string;
  mimeType: string;
  filename: string;
}

// ============================================================================
// Scene Voiceover Types
// ============================================================================

/**
 * Scene voiceover entity from database
 * Represents a generated TTS audio file for a reel scene
 */
export interface ISceneVoiceover {
  _id: Id<'sceneVoiceovers'>;
  _creationTime: number;
  sceneId: Id<'reelScenes'>;
  reelId: Id<'reels'>;
  userId: Id<'users'>;
  voiceId: Id<'clonedVoices'>;
  elevenLabsVoiceId: string;
  audioStorageId: Id<'_storage'>;
  audioUrl?: string; // Populated URL from storage
  durationSeconds: number;
  fileSizeBytes: number;
  format: string;
  voiceoverText: string;
  voiceSettingsSnapshot: IVoiceSettings;
  status: VoiceoverStatus;
  errorMessage?: string;
  generatedAt: number;
  updatedAt: number;
}

/**
 * Result of voiceover generation action
 */
export interface IVoiceoverGenerationResult {
  success: boolean;
  voiceoverId?: Id<'sceneVoiceovers'>;
  audioUrl?: string;
  durationSeconds?: number;
  error?: string;
  errorCode?: string;
}

/**
 * Result of bulk voiceover generation action
 */
export interface IBulkVoiceoverResult {
  success: boolean;
  totalScenes: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  results: Array<{
    sceneId: Id<'reelScenes'>;
    success: boolean;
    voiceoverId?: Id<'sceneVoiceovers'>;
    error?: string;
  }>;
  error?: string;
}

// ============================================================================
// Voice Assignment Types
// ============================================================================

/**
 * Scene voice override
 * Allows different voices for specific scenes in a reel
 */
export interface ISceneVoiceOverride {
  sceneId: Id<'reelScenes'>;
  voiceId: Id<'clonedVoices'>;
}

/**
 * Reel voice assignment entity from database
 * Links voices to reels with optional per-scene overrides
 */
export interface IReelVoiceAssignment {
  _id: Id<'reelVoiceAssignments'>;
  _creationTime: number;
  reelId: Id<'reels'>;
  userId: Id<'users'>;
  defaultVoiceId?: Id<'clonedVoices'>;
  sceneOverrides?: ISceneVoiceOverride[];
  createdAt: number;
  updatedAt: number;
}

/**
 * Result of voice assignment mutation
 */
export interface IVoiceAssignmentResult {
  success: boolean;
  assignmentId?: Id<'reelVoiceAssignments'>;
  error?: string;
}

// ============================================================================
// Hook State Types
// ============================================================================

/**
 * Voice cloning workflow step
 */
export type VoiceCloneStep =
  | 'idle'
  | 'validating'
  | 'uploading'
  | 'cloning'
  | 'completed'
  | 'error';

/**
 * Voiceover generation workflow step
 */
export type VoiceoverGenerationStep = 'idle' | 'generating' | 'completed' | 'error';

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Voice card component props
 */
export interface IVoiceCardProps {
  voice: IClonedVoice;
  isSelected?: boolean;
  onSelect?: (voiceId: Id<'clonedVoices'>) => void;
  onDelete?: (voiceId: Id<'clonedVoices'>) => void;
  onEditSettings?: (voiceId: Id<'clonedVoices'>) => void;
  onRefreshPreview?: (voiceId: Id<'clonedVoices'>) => Promise<void>;
  isRefreshingPreview?: boolean;
}

/**
 * Voice list component props
 */
export interface IVoiceListProps {
  voices: IClonedVoice[];
  selectedVoiceId?: Id<'clonedVoices'>;
  onSelectVoice?: (voiceId: Id<'clonedVoices'>) => void;
  onDeleteVoice?: (voiceId: Id<'clonedVoices'>) => void;
  onRefreshPreview?: (voiceId: Id<'clonedVoices'>) => Promise<void>;
  refreshingVoiceId?: Id<'clonedVoices'> | null;
  isLoading?: boolean;
}

/**
 * Voice clone form component props
 */
export interface IVoiceCloneFormProps {
  onSubmit: (data: IVoiceCloneFormInput) => Promise<void>;
  isSubmitting: boolean;
  onCancel?: () => void;
}

/**
 * Audio file upload component props
 */
export interface IAudioFileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxFileSizeMB?: number;
  acceptedTypes?: string[];
  disabled?: boolean;
}

/**
 * Voice settings editor component props
 */
export interface IVoiceSettingsEditorProps {
  voiceId: Id<'clonedVoices'>;
  settings: IVoiceSettings;
  onSave: (settings: IVoiceSettings) => Promise<void>;
  isSaving: boolean;
}

/**
 * Voice preview player component props
 */
export interface IVoicePreviewPlayerProps {
  previewUrl: string | undefined;
  voiceName: string;
  className?: string;
  onGeneratePreview?: () => Promise<void>;
  isGeneratingPreview?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Voice feature constants
 * Used for validation and UI constraints
 */
export const VOICE_CONSTANTS = {
  // Voice limits
  MAX_VOICES_PER_USER: 10,
  MAX_AUDIO_FILES: 25,
  MIN_AUDIO_FILES: 1,
  MAX_AUDIO_FILE_SIZE_MB: 10,
  MAX_AUDIO_FILE_SIZE_BYTES: 10 * 1024 * 1024, // 10MB in bytes

  // Audio requirements
  ACCEPTED_AUDIO_TYPES: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/webm', 'audio/ogg'],
  ACCEPTED_AUDIO_EXTENSIONS: ['.mp3', '.wav', '.m4a', '.webm', '.ogg'],
  MIN_AUDIO_DURATION_SECONDS: 30, // Recommended minimum

  // Text limits
  MAX_NAME_LENGTH: 50,
  MAX_DESCRIPTION_LENGTH: 500,
  MAX_VOICEOVER_TEXT_LENGTH: 5000,

  // Default voice settings
  DEFAULT_VOICE_SETTINGS: {
    stability: 0.5,
    similarityBoost: 0.75,
    style: 0.5,
    useSpeakerBoost: true,
  } as IVoiceSettings,

  // ElevenLabs configuration
  TTS_MODEL_ID: 'eleven_multilingual_v2',
  AUDIO_OUTPUT_FORMAT: 'mp3_44100_128',
} as const;

/**
 * Voice settings slider configuration
 */
export const VOICE_SETTINGS_CONFIG = {
  stability: {
    min: 0,
    max: 1,
    step: 0.01,
    defaultValue: 0.5,
    label: 'Stability',
    description: 'Lower values are more variable, higher values are more consistent',
  },
  similarityBoost: {
    min: 0,
    max: 1,
    step: 0.01,
    defaultValue: 0.75,
    label: 'Similarity Boost',
    description: 'Higher values make the voice sound closer to the original',
  },
  style: {
    min: 0,
    max: 1,
    step: 0.01,
    defaultValue: 0.5,
    label: 'Style Exaggeration',
    description: 'Higher values make the speech more expressive',
  },
} as const;
